import { useEffect, useRef, useState, useCallback } from "react";
import classnamesBind from "classnames/bind";
import styles from "./Modal.scss";
import getWindow from "../../utils/window";
import {
  MODAL_HEADER_STYLE,
  MODAL_TYPE,
  MODAL_ANIMATION_TIME,
} from "../../constants";
import BrandedSpinner from "../spinner/branded/BrandedSpinner";
import useEventListener from "../../hooks/useEventListener";
import useTrapFocus from "../../hooks/useTrapFocus";
import ModalOverlay from "../modalOverlay/ModalOverlay";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { ModalProps } from "../../types/components/modal";

const { SIDE, CENTER, CENTER_LARGE, BOTTOM } = MODAL_TYPE;

const classnames = classnamesBind.bind(styles);

export default function Modal({
  title,
  subtitle,
  open,
  contentLoaded,
  focusOnClose,
  closeModal,
  children,
  modalId,
  handleOverlayClick,
  overlayOpen,
  modalType,
  withScrollBar = true,
  hideCloseButton,
  forceDOMRewrite,
  useFocusTrapCatchAll,
  withDarkThemeSupport = false,
  modalHeaderStyle = MODAL_HEADER_STYLE.LIGHT,
  additionalClasses,
  onHeaderHeightChange,
}: ModalProps) {
  const [shouldRender, setShouldRender] = useState(open);
  const [shouldRenderOverlay, setShouldRenderOverlay] = useState(open);
  const [resetTrap, setResetTrap] = useState(false);
  const derenderTimeout = useRef<NodeJS.Timeout | null>(null);
  const firstRender = useRef(true);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
  };
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === 27 || event.key === "escape") {
      if (!overlayOpen) {
        closeModal();
      }
    }
  };

  const clickOverlay = () => {
    closeModal();
    handleOverlayClick && handleOverlayClick();
  };

  useEventListener("keydown", handleKeyDown, []);

  useEffect(() => {
    const derenderCallback = () => {
      const derender = () => {
        setShouldRender(!forceDOMRewrite);
        setShouldRenderOverlay(false);
        getWindow().document.body.style.overflow = "";
        focusOnClose?.focus();
      };
      if (modalType === SIDE) {
        clearTimeout(derenderTimeout.current as NodeJS.Timeout);
        derenderTimeout.current = setTimeout(() => {
          derender();
        }, MODAL_ANIMATION_TIME);
        return;
      }
      derender();
    };

    if (!open && !firstRender.current) {
      derenderCallback();
    } else if (!firstRender.current) {
      clearTimeout(derenderTimeout.current as NodeJS.Timeout);
      getWindow().document.body.style.overflow = "hidden";
      setShouldRenderOverlay(true);
      setShouldRender(true);
    }
  }, [open, modalType, forceDOMRewrite, focusOnClose]);

  const focusTimeoutRef = useRef<number | null>(null);

  const closeButton = useCallback(
    (node: HTMLButtonElement | null) => {
      closeButtonRef.current = node;
      if (open && node) {
        focusTimeoutRef.current = getWindow().setTimeout(() => {
          node.focus();
        }, MODAL_ANIMATION_TIME);
      }

      if (!node) {
        const win = getWindow();
        if (win) {
          win.clearTimeout(focusTimeoutRef.current as number);
        }
      }
    },
    [open]
  );

  useEffect(() => {
    if (!contentLoaded && open && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [open, contentLoaded]);

  useEffect(() => {
    setResetTrap(shouldRender && contentLoaded);
  }, [shouldRender, contentLoaded]);

  useEffect(() => {
    firstRender.current = false;
  }, []);

  const setModalRef = useTrapFocus({
    resetTrap,
    useCatchAll: useFocusTrapCatchAll,
  });

  const getModalTypeStyle = () => ({
    center: modalType === CENTER || modalType === CENTER_LARGE,
    large: modalType === CENTER_LARGE,
    side: modalType === SIDE,
    bottom: modalType === BOTTOM,
  });

  const renderCloseButton = () => (
    <button
      className={styles.close}
      aria-label={getTranslation("pdp_misc_close")}
      onClick={closeModal}
      data-testid="closeModal"
      ref={closeButton}
    >
      <div className={styles.icon} />
    </button>
  );

  const renderOverlay = () =>
    shouldRenderOverlay && (
      <div
        className={classnames(styles.overlay, {
          stopScroll: overlayOpen,
          open: open,
          ...getModalTypeStyle(),
        })}
        aria-hidden={true}
        onClick={clickOverlay}
        data-testid="modalOverlay"
      />
    );

  const renderModalHeader = ({ style }: { style: string }) => {
    if (hideCloseButton && contentLoaded) {
      return null;
    }

    return (
      <div
        className={classnames(
          styles.stickyContainer,
          styles.modalHeader,
          style
        )}
        ref={(node) => {
          if (node) {
            const height = node.offsetHeight;
            if (height !== stickyHeaderHeight) {
              setStickyHeaderHeight(height);
              onHeaderHeightChange?.(height);
            }
          }
          node && !contentLoaded && setModalRef(node);
        }}
      >
        {renderCloseButton()}
        {title && (
          <h2
            id="modalTitle"
            className={classnames(styles.title, additionalClasses)}
          >
            {title}
          </h2>
        )}
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
    );
  };

  const modalStyles = () => ({
    overflowY: withScrollBar ? ("scroll" as const) : ("hidden" as const),
    scrollPaddingTop: `${stickyHeaderHeight}px`,
  });

  return shouldRender ? (
    <>
      {renderOverlay()}
      <div
        id={modalId}
        data-testid={modalType}
        className={classnames(styles.modal, {
          open,
          withDarkThemeSupport: contentLoaded && withDarkThemeSupport,
          ...getModalTypeStyle(),
        })}
        style={modalStyles()}
        ref={(node) => {
          if (node && contentLoaded) {
            setModalRef(node);
          }
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modalTitle"
      >
        {!contentLoaded && (
          <ModalOverlay modalId={modalId}>
            <BrandedSpinner colour={"black"} />
          </ModalOverlay>
        )}
        {renderModalHeader({ style: modalHeaderStyle })}
        {children}
      </div>
    </>
  ) : null;
}
